import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { getTipsForPath } from '../data/tipsContent'

function TipsPanel() {
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const sessionOpenRef = useRef(false)
  const panelRef = useRef(null)
  const backdropRef = useRef(null)

  const tips = getTipsForPath(location.pathname)

  // When route changes, restore session open state if panel was open
  useEffect(() => {
    if (sessionOpenRef.current) {
      const newTips = getTipsForPath(location.pathname)
      if (!newTips) {
        setOpen(false)
        sessionOpenRef.current = false
      }
    }
  }, [location.pathname])

  const openPanel = useCallback(() => {
    setOpen(true)
    sessionOpenRef.current = true
  }, [])

  const closePanel = useCallback(() => {
    setOpen(false)
    sessionOpenRef.current = false
  }, [])

  const togglePanel = useCallback(() => {
    if (open) {
      closePanel()
    } else {
      openPanel()
    }
  }, [open, openPanel, closePanel])

  // Click outside to close
  useEffect(() => {
    if (!open) return
    function handleClick(e) {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        !e.target.closest('.tips-trigger')
      ) {
        closePanel()
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open, closePanel])

  // Escape key to close
  useEffect(() => {
    if (!open) return
    function handleKey(e) {
      if (e.key === 'Escape') closePanel()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, closePanel])

  if (!tips) return null

  return (
    <>
      {/* Mobile backdrop */}
      <div
        ref={backdropRef}
        className={`tips-backdrop${open ? ' tips-backdrop--visible' : ''}`}
        onClick={closePanel}
        aria-hidden="true"
      />

      {/* Trigger button */}
      <button
        className={`tips-trigger${open ? ' tips-trigger--active' : ''}`}
        onClick={togglePanel}
        aria-label="Toggle tips panel"
        title="Tips"
      >
        <span className="tips-trigger-icon">?</span>
        <span className="tips-trigger-badge">{tips.tips.length}</span>
      </button>

      {/* Sliding panel */}
      <aside
        ref={panelRef}
        className={`tips-panel${open ? ' tips-panel--open' : ''}`}
        aria-label="Tips panel"
        aria-hidden={!open}
      >
        {/* Header */}
        <div className="tips-panel-header">
          <div className="tips-panel-header-inner">
            <span className="tips-panel-eyebrow">Tips</span>
            <h2 className="tips-panel-title">{tips.pageLabel}</h2>
          </div>
          <button
            className="tips-panel-close"
            onClick={closePanel}
            aria-label="Close tips"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="tips-panel-body">
          {tips.intro && (
            <p className="tips-panel-intro">{tips.intro}</p>
          )}
          <ol className="tips-list">
            {tips.tips.map((tip, i) => (
              <li key={i} className="tips-card">
                <div className="tips-card-num">{i + 1}</div>
                <div className="tips-card-content">
                  <strong className="tips-card-title">{tip.title}</strong>
                  <p className="tips-card-body">{tip.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </aside>
    </>
  )
}

export default TipsPanel
